/**
 * StudentInvoices — FM3F Student Invoice View
 *
 * Route: /student/invoices
 * RBAC:  finance.invoice.view.own (student only)
 *
 * Students see ISSUED invoices only — no DRAFT, no pending liability exposure.
 */
import React, { useEffect, useState } from "react";
import {
  FileText, RefreshCw, ChevronRight, Shield, CheckCircle2
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from "../components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { Separator } from "../components/ui/separator";

function fmt(amount) {
  if (amount == null) return "—";
  return parseFloat(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function StudentInvoices() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const canView = user?.role === "student";

  useEffect(() => {
    if (!canView) return;
    setLoading(true);
    api.get("/student/me/finance/invoices")
      .then(({ data: d }) => setInvoices(d.invoices || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [canView]);

  const openDetail = async (inv) => {
    setDetailLoading(true);
    setSelected(null);
    try {
      const { data: d } = await api.get(`/student/me/finance/invoices/${inv.id}`);
      setSelected(d);
    } catch (e) {
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (!canView) {
    return (
      <ResponsivePageContainer>
        <div className="py-24 text-center text-slate-500">
          <Shield className="w-8 h-8 mx-auto mb-3 text-slate-400" />
          <p className="text-sm">This section is for students only.</p>
        </div>
      </ResponsivePageContainer>
    );
  }

  return (
    <ResponsivePageContainer>
      <PageHeader
        title="My Invoices"
        description="Your issued tuition invoices and fee obligations."
        data-testid="student-invoices-page-header"
      />

      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading invoices…
        </div>
      ) : invoices.length === 0 ? (
        <div className="py-20 text-center" data-testid="no-invoices-message">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No invoices yet.</p>
          <p className="text-slate-400 text-sm mt-1">Your fee invoices will appear here once issued.</p>
        </div>
      ) : (
        <div className="mt-6 rounded-lg border overflow-hidden" data-testid="student-invoices-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Invoice #</TableHead>
                <TableHead>Issue Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map(inv => (
                <TableRow
                  key={inv.id}
                  className="hover:bg-slate-50/50 cursor-pointer"
                  data-testid={`student-invoice-row-${inv.id}`}
                >
                  <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                  <TableCell className="text-sm">{inv.invoice_date}</TableCell>
                  <TableCell className="text-sm">{inv.due_date}</TableCell>
                  <TableCell className="text-right font-bold text-sm">
                    {inv.currency_iso} {fmt(inv.total_amount)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => openDetail(inv)}
                      data-testid={`student-invoice-detail-btn-${inv.id}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Invoice Detail Sheet */}
      <Sheet
        open={!!selected || detailLoading}
        onOpenChange={v => { if (!v) setSelected(null); }}
      >
        <SheetContent side="right" className="w-full sm:w-[480px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle data-testid="student-invoice-detail-title">
              {detailLoading ? "Loading…" : `Invoice ${selected?.invoice_number || ""}`}
            </SheetTitle>
          </SheetHeader>

          {selected && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Invoice Date</span><div>{selected.invoice_date}</div></div>
                <div><span className="text-slate-500">Due Date</span><div className="font-medium">{selected.due_date}</div></div>
                <div><span className="text-slate-500">Currency</span><div>{selected.currency_iso}</div></div>
                <div><span className="text-slate-500">Total</span><div className="font-bold text-base">{selected.currency_iso} {fmt(selected.total_amount)}</div></div>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Fee Breakdown</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selected.lines || []).map(l => (
                      <TableRow key={l.id} data-testid={`student-invoice-line-${l.id}`}>
                        <TableCell className="text-sm">{l.description}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{fmt(l.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {selected.installment_plan && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Payment Schedule</h4>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>#</TableHead>
                          <TableHead>Due</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selected.installment_plan.lines.map(l => (
                          <TableRow key={l.id} data-testid={`student-installment-line-${l.id}`}>
                            <TableCell className="text-xs">{l.installment_number}</TableCell>
                            <TableCell className="text-xs">{l.due_date}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(l.amount)}</TableCell>
                            <TableCell>
                              <span className={`text-xs font-medium ${l.status === "PAID" ? "text-emerald-600" : "text-amber-600"}`}>
                                {l.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </ResponsivePageContainer>
  );
}
